import { isMonetizedLink, isOutboundLink } from './monetization';

/**
 * Add a leading slash to the given path
 *
 * @param {string} path path to modify
 * @returns {string} path with leading slash
 */
const addLeadingSlash = path =>
  typeof path === 'string' ? `/${path.replace(/^\//, '')}` : '';

/**
 * Prepend the given domain to the path
 *
 * @param {string} path path to prepend with the domain
 * @param {string} [domain="https://www.fundera.com"] domain to use
 * @returns {string} path with prepended domain
 */
const prependDomain = (path, domain = 'https://www.fundera.com') => {
  if (typeof path !== 'string') return '';
  const funderaDomain = domain || 'https://www.fundera.com';
  return path.match(/((https|http)?:\/\/(w{3}\.)?|(w{3}\.)).+/g)
    ? path
    : `${funderaDomain}${addLeadingSlash(path)}`;
};

/**
 * ensuring parameter is part of the url
 * all values are require
 * @param {string} url current url
 * @param {string} parameter parameter to add if needed
 * @param {string|Object} value value for the parameter
 * @returns {string} new url
 */
const ensureParameter = (url, parameter, value) => {
  if (
    !url ||
    typeof url !== 'string' ||
    !parameter ||
    typeof parameter !== 'string' ||
    !value ||
    !['string', 'object'].includes(typeof value)
  )
    return url;

  const parameterEq = `${parameter}=`;
  const splitString = url.split(parameterEq);
  const parameterConSym = url.match(/\?/g) ? `&` : `?`;

  const replaceValue = typeof value === 'string' ? value : value.value;
  const result =
    splitString.length === 1
      ? `${splitString[0]}${parameterConSym}${parameterEq}${replaceValue}`
      : url;

  if (value.force) {
    return result.replace(
      new RegExp(`${parameterEq}.+?(&|$)`),
      `${parameterEq}${replaceValue}$1`
    );
  }

  return result;
};

/**
 * Add query params to the given URL
 *
 * @param {string} url URL to append the query params to
 * @param {Object} queryParams query params to to append to URL
 * @returns {string} URL with appended query params
 */
const addQueryParams = (url, queryParams) => {
  return Object.keys(queryParams).reduce(
    (processedURL, paramName) =>
      paramName &&
      typeof queryParams[paramName] !== 'undefined' &&
      queryParams[paramName] !== null
        ? ensureParameter(processedURL, paramName, queryParams[paramName])
        : processedURL,
    url
  );
};

/**
 * Prepend the given domain to the path
 *
 * @param {string} path path to prepend with the domain
 * @param {options} options options to consider for update
 * @param {string} [options.placement] path of the current page
 * @param {string} [options.placement_type] placement type to use
 * @param {string} [options.domain="https://www.fundera.com"] domain to use
 * @returns {string} path with prepended domain
 */
const updateWithDomainAndQueryParams = (path, options) => {
  const { domain = 'https://www.fundera.com', ...params } = options;
  const fullUrl = prependDomain(path, domain);

  /** @fullUrlAndPlacement {string} URL with necessary query params */
  const fullUrlAndPlacement = isOutboundLink(path)
    ? addQueryParams(fullUrl, params)
    : fullUrl;

  return fullUrlAndPlacement;
};
/**
 * Get the real content URL from a standard CMS post
 *
 * @param {Object} post post object
 * @returns {string} relative URL for the given post
 */
const getPageUrl = post => {
  let path = post.uri;
  if (post && post.canonical_url) {
    path = post.canonical_url;
  }

  if (
    post &&
    post.fdsPage &&
    post.fdsPage.pageSettings &&
    post.fdsPage.pageSettings.canonicalUrl
  ) {
    path = post.fdsPage.pageSettings.canonicalUrl.trim();
  }

  return addLeadingSlash(path);
};

export {
  addLeadingSlash,
  getPageUrl,
  prependDomain,
  updateWithDomainAndQueryParams,
  isOutboundLink,
  isMonetizedLink,
};
